// Hastalar — admin klinik detayı sekmesi. Tek veri akışı → düz (kutusuz) sekme
// (design-principles §1, §3): araç satırı (tek arama: rakamsa TC, değilse ad/soyad
// + doğum tarihi) → çerçevesiz tablo. Sayfalama sunucuda. Satır tıklanınca
// hastanın admin detayına gider. Listede TC maskeli (KVKK).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FaUserInjured } from "react-icons/fa";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useListPatientsMutation } from "../../../store/secretaryApi";
import { useHasPermission } from "../../../hooks/useHasPermission";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";
import {
  FormDatePicker,
  SearchField,
  formatPhoneDisplay,
} from "../../../components/atoms";
import EmptyState from "../../../components/atoms/EmptyState";
import { DataTableCard } from "../../../components/organisms";
import {
  ClickableRow,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  EDGE_L,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  StatusPill,
  TextCell,
  cellSx,
} from "../../../components/organisms/ClinicRecordList";

const COLS = 3;

const STATUS_TONE = {
  Aktif: "success",
  "Askıya Alındı": "warning",
  Beklemede: "warning",
  "Devre Dışı": "error",
  Reddedildi: "error",
};

export default function PatientsTab({ clinicId, token, palette }) {
  const navigate = useNavigate();
  const canView = useHasPermission("admin-clinics:view-patients");
  const [listPatients, { isLoading }] = useListPatientsMutation();

  const [search, setSearch] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const { debounced: debouncedSearch, pending: searchPending } =
    useDebouncedSearch(search.trim());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token || !clinicId || !canView) return;
    let active = true;
    (async () => {
      try {
        const payload = { token, clinicId, page: page + 1, limit: rowsPerPage };
        if (debouncedSearch) {
          if (/^\d+$/.test(debouncedSearch)) payload.tcNo = debouncedSearch;
          else payload.query = debouncedSearch;
        }
        if (birthDay) payload.birthDay = birthDay;
        const res = await listPatients(payload).unwrap();
        if (!active) return;
        setItems(res?.data?.patients || []);
        setTotal(res?.data?.pagination?.total || 0);
      } catch (_e) {
        if (active) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    token,
    clinicId,
    canView,
    debouncedSearch,
    birthDay,
    page,
    rowsPerPage,
    listPatients,
  ]);

  if (!canView) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: COLORS.white,
        }}
      >
        <EmptyState
          variant="illustrated"
          icon={<FaUserInjured />}
          title="Hasta listesi görüntülenemiyor"
          description="Bu bölümü görüntüleme yetkiniz yok."
        />
      </Box>
    );
  }

  // İlk yükleme, yazarken (debounce) ve her yeni istekte iskelet.
  const busy = !loaded || isLoading || searchPending;
  const empty = debouncedSearch
    ? {
        title: "Hasta bulunamadı",
        message: `“${debouncedSearch}” aramanızla eşleşen hasta yok. Aramayı değiştirin.`,
      }
    : birthDay
      ? {
          title: "Hasta bulunamadı",
          message: "Bu doğum tarihine sahip hasta yok.",
        }
      : {
          title: "Henüz hasta yok",
          message:
            "Klinik hasta ekledikçe veya takip talepleri onaylandıkça burada listelenir.",
        };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: COLORS.white,
      }}
    >
      {/* Araç satırı */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <SearchField
          placeholder="Ad, soyad veya TC no ile ara"
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          autoComplete="off"
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
        <FormDatePicker
          placeholder="Doğum tarihi"
          value={birthDay}
          onChange={(v) => {
            setBirthDay(v);
            setPage(0);
          }}
          maxDate={new Date().toISOString().slice(0, 10)}
          sx={{ width: 180 }}
        />
        <Box sx={{ flex: 1 }} />
        {loaded ? (
          <Typography
            sx={{
              fontSize: TYPO.body,
              color: COLORS.slate500,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {total.toLocaleString("tr-TR")} hasta
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <DataTableCard
          loading={Boolean(busy)}
          variant="flat"
          empty={!busy && items.length === 0}
          emptyIcon={<FaUserInjured />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={total}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: "fixed", minWidth: 760 }}
          >
            <TableHead>
              <TableRow>
                <PlainHead sx={{ ...EDGE_L, width: "44%" }}>Hasta</PlainHead>
                <PlainHead sx={{ width: "40%" }}>İletişim</PlainHead>
                <PlainHead sx={{ ...EDGE_R, width: "16%" }}>Durum</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows rows={Math.min(10, rowsPerPage)} cols={COLS} />
              ) : items.length ? (
                items.map((p) => (
                  <ClickableRow
                    key={p._id}
                    onClick={() =>
                      navigate(`/Klinikler/${clinicId}/Hastalar/${p._id}`)
                    }
                    palette={palette}
                  >
                    <PatientCell patient={p} />
                    <TextCell
                      main={
                        p.tellNo
                          ? formatPhoneDisplay(p.tellNo)
                          : p.email || null
                      }
                      sub={p.tellNo ? p.email || null : null}
                    />
                    <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                      <StatusPill
                        label={p.accountStatus || "—"}
                        tone={STATUS_TONE[p.accountStatus]}
                        palette={palette}
                      />
                    </TableCell>
                  </ClickableRow>
                ))
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </Box>
    </Box>
  );
}
